import React, { useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TextField } from '@atoms';
import { trpc } from '@lib/trpc';
import { SupplierRecord } from '@types';
import { LoadingSpinner } from '@atoms';
import { format } from 'date-fns';

// No longer using MUI theme - migrating to atomic components

export const SupplierList: React.FC = () => {
  // Updated to use Material-UI TextField for consistent styling
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // tRPC queries
  const { data: suppliersResponse, isLoading } = trpc.supplier.getAll.useQuery(undefined, {
    refetchInterval: false,
  });

  const searchSuppliersQuery = trpc.supplier.search.useQuery(
    { term: searchTerm },
    { 
      enabled: searchTerm.length > 0,
      refetchInterval: false,
    }
  );

  // Extract suppliers from response
  const allSuppliers = suppliersResponse?.data || [];
  const searchResults = searchSuppliersQuery.data?.data || [];

  // Filter suppliers based on search
  const filteredSuppliers = searchTerm.length > 0 ? searchResults : allSuppliers;

  const handleSupplierClick = (supplier: SupplierRecord) => {
    navigate(`/suppliers/${supplier.id}/edit`);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="w-8 h-8 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="max-w-md relative">
          <TextField
            placeholder="Search suppliers..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            icon={searchTerm && searchSuppliersQuery.isLoading ? Loader2 : Search}
            iconPosition="left"
            iconClassName={searchTerm && searchSuppliersQuery.isLoading ? 'animate-spin' : ''}
          />
        </div>
      </div>

      {/* Suppliers List */}
      <div className="bg-white rounded-lg shadow overflow-hidden relative">
        {/* Loading overlay for search */}
        {searchTerm && searchSuppliersQuery.isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <LoadingSpinner size="md" text="Searching suppliers..." />
          </div>
        )}
        
        {filteredSuppliers.length === 0 && !searchSuppliersQuery.isLoading ? (
          <div className="text-center py-8 text-secondary-500">
            {searchTerm ? 'No suppliers found matching your search.' : 'No suppliers found.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-secondary-200">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Supplier Name
                  </th>
                  <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    ABN
                  </th>
                  <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Terms
                  </th>
                  <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Last Invoice
                  </th>
                  <th className="w-1/12 px-6 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    In progress
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-200">
                {filteredSuppliers.map((supplier: SupplierRecord) => (
                  <tr 
                    key={supplier.id} 
                    className="hover:bg-secondary-50 cursor-pointer transition-colors duration-150"
                    onClick={() => handleSupplierClick(supplier)}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-secondary-900 truncate">
                        {supplier.name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-secondary-900 truncate">
                        {supplier.abn || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-secondary-900 truncate">
                        {supplier.terms || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-secondary-900 truncate">
                        {formatDate(supplier.last_invoice_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {supplier.unprocessed_invoices_count > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {supplier.unprocessed_invoices_count}
                        </span>
                      ) : (
                        <span className="text-sm text-secondary-500">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Show search state */}
      {searchTerm && (
        <div className="text-sm text-secondary-500 text-center">
          {searchSuppliersQuery.isLoading ? 'Searching...' : `Found ${filteredSuppliers.length} suppliers`}
        </div>
      )}
    </div>
  );
};
